import { Box } from "@mui/material";
import Link from "next/link";
import { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFlightHubAccessAllowed } from "src/domains/shared/constants/flighthubAccess";
import { useAuth } from "src/domains/shared/hooks/user";
import { hasStoredLogin } from "src/domains/shared/utils/exclusiveLogin/loginClaimPending";
import { LOGIN_ROUTE } from "src/domains/shared/utils/exclusiveLogin/loginRoutes";
import { SiteButtonSpinner } from "../../siteKit/SiteButtonSpinner";
import { SITE_SESSION_ATTR } from "../siteRoutes";
import { useFlightHubEntry } from "./useFlightHubEntry";
import { SITE_APP_START_HREF } from "./siteNav";
import {
  SITE_ACCOUNT_BUTTON_STYLES,
  SITE_SESSION_ONLY_ATTR,
} from "./siteHeaderStyles";

/** 로그인 확인 중(`checking`)에는 계정 버튼을 두 쪽 다 그리고, 문서의 저장된 로그인 판정(`SITE_SESSION_ATTR`)으로 한쪽만 보인다 */
export type SiteSessionStatus = "checking" | "signedIn" | "signedOut";

export interface SiteAccountState {
  sessionStatus: SiteSessionStatus;
  isFlightHubVisible: boolean;
  isEnteringFlightHub: boolean;
  enterFlightHub: () => void;
  /** 로그아웃 중 — 끝나 「로그인」 으로 바뀔 때까지 「로그아웃」 에 진행 표시를 단다 */
  isSigningOut: boolean;
  logout: () => void;
}

/** 머리말 오른쪽·모바일 메뉴가 함께 쓰는 로그인 상태. 관제 진입은 허용 도메인 사용자만(flighthub2-sso-access). */
export function useSiteAccount(): SiteAccountState {
  const { currentUser, isLoading, signOut } = useAuth();
  const { enterFlightHub, isEntering } = useFlightHubEntry();
  const [isSigningOut, setIsSigningOut] = useState(false);

  // 부트 스크립트는 문서를 처음 받을 때만 돈다. 로그인 뒤 홈으로 오는 등 클라이언트 이동으로 머리말이 새로 붙을 때도
  // 확인 전 계정 버튼이 맞게, 칠하기 전에 지금 저장된 로그인으로 다시 맞춘다.
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute(SITE_SESSION_ATTR, hasStoredLogin());
  }, []);

  const logout = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  const sessionStatus: SiteSessionStatus = (() => {
    if (currentUser) return "signedIn";
    return isLoading ? "checking" : "signedOut";
  })();

  return {
    sessionStatus,
    isFlightHubVisible:
      Boolean(currentUser) &&
      isFlightHubAccessAllowed(currentUser?.attributes?.email),
    isEnteringFlightHub: isEntering,
    enterFlightHub: () => {
      void enterFlightHub();
    },
    isSigningOut,
    logout: () => {
      void logout();
    },
  };
}

type SessionSide = "signed-in" | "signed-out";

/** 확인 중에만 버튼에 어느 쪽인지 단다 — 확인이 끝나면 한쪽만 그리므로 떼어 낸다 */
const sessionOnly = (status: SiteSessionStatus, side: SessionSide) =>
  status === "checking" ? { [SITE_SESSION_ONLY_ATTR]: side } : undefined;

/** 데스크톱 머리말 오른쪽 버튼 — 로그인 전 「로그인」, 로그인 뒤 (관제 진입) 「시작하기」 「로그아웃」 */
export function AccountActions({ account }: { account: SiteAccountState }) {
  const { t } = useTranslation("landing");
  const { sessionStatus } = account;

  const signedOutActions = (
    <Box
      component={Link}
      href={LOGIN_ROUTE}
      sx={SITE_ACCOUNT_BUTTON_STYLES.primary}
      {...sessionOnly(sessionStatus, "signed-out")}
    >
      {t("site.account.login")}
    </Box>
  );
  const signedInActions = (
    <>
      {account.isFlightHubVisible && (
        <Box
          component="button"
          type="button"
          onClick={account.enterFlightHub}
          disabled={account.isEnteringFlightHub}
          aria-busy={account.isEnteringFlightHub || undefined}
          sx={SITE_ACCOUNT_BUTTON_STYLES.outline}
        >
          {t("site.account.flightHub")}
        </Box>
      )}
      <Box
        component={Link}
        href={SITE_APP_START_HREF}
        sx={SITE_ACCOUNT_BUTTON_STYLES.primary}
        {...sessionOnly(sessionStatus, "signed-in")}
      >
        {t("site.account.start")}
      </Box>
      <Box
        component="button"
        type="button"
        onClick={account.logout}
        disabled={account.isSigningOut}
        aria-busy={account.isSigningOut || undefined}
        sx={[
          SITE_ACCOUNT_BUTTON_STYLES.outline,
          account.isSigningOut && SITE_ACCOUNT_BUTTON_STYLES.inProgress,
        ]}
        {...sessionOnly(sessionStatus, "signed-in")}
      >
        {account.isSigningOut ? <SiteButtonSpinner /> : null}
        {t("site.account.logout")}
      </Box>
    </>
  );

  if (sessionStatus === "signedOut") return signedOutActions;
  if (sessionStatus === "signedIn") return signedInActions;
  return (
    <>
      {signedOutActions}
      {signedInActions}
    </>
  );
}

/** 좁은 화면 메뉴 목록의 로그인 상태 항목 — 데스크톱 머리말과 같은 분기·같은 확인 중 처리 */
export function MobileAccountItems({
  account,
  onNavigate,
}: {
  account: SiteAccountState;
  /** 링크로 다른 화면에 갈 때 메뉴를 닫는다 */
  onNavigate: () => void;
}) {
  const { t } = useTranslation("landing");
  const { sessionStatus } = account;

  const signedOutItems = (
    <Link
      href={LOGIN_ROUTE}
      data-primary=""
      onClick={onNavigate}
      {...sessionOnly(sessionStatus, "signed-out")}
    >
      {t("site.account.login")}
    </Link>
  );
  const signedInItems = (
    <>
      {account.isFlightHubVisible && (
        <button
          type="button"
          onClick={account.enterFlightHub}
          disabled={account.isEnteringFlightHub}
          aria-busy={account.isEnteringFlightHub || undefined}
        >
          {t("site.account.flightHub")}
        </button>
      )}
      <Link
        href={SITE_APP_START_HREF}
        data-primary=""
        onClick={onNavigate}
        {...sessionOnly(sessionStatus, "signed-in")}
      >
        {t("site.account.start")}
      </Link>
      <button
        type="button"
        onClick={account.logout}
        disabled={account.isSigningOut}
        aria-busy={account.isSigningOut || undefined}
        {...sessionOnly(sessionStatus, "signed-in")}
      >
        {account.isSigningOut ? <SiteButtonSpinner /> : null}
        {t("site.account.logout")}
      </button>
    </>
  );

  if (sessionStatus === "signedOut") return signedOutItems;
  if (sessionStatus === "signedIn") return signedInItems;
  return (
    <>
      {signedOutItems}
      {signedInItems}
    </>
  );
}
